import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Logo } from '../components/Logo';
import {
    FileText, Sparkles, CreditCard, UserCheck, Mail, ArrowRight, ArrowLeft,
    CheckCircle2, AlertTriangle, Stamp, ShieldCheck, Clock, Download,
} from 'lucide-react';

const SECTIONS = [
    {
        step: 1,
        icon: FileText,
        title: 'Compila il form di richiesta',
        time: '~1 minuto',
        body: 'Dalla pagina "Richiedi DVR" inserisci i dati della tua impresa (ragione sociale, email, P.IVA) e descrivi il lavoro: tipologia, descrizione dettagliata (min 20 caratteri), n. lavoratori, durata in giorni, altezza massima di lavoro, luogo. Scegli infine il livello di urgenza (Standard / Veloce +25% / Urgente +60%).',
        tips: [
            'Più dettagli fornisci sulla descrizione, più precisa sarà la valutazione AI',
            'Non serve essere tecnici: scrivi nel tuo linguaggio, l\'AI capisce',
            'Tutti i parametri tecnici sono opzionali tranne tipologia e descrizione',
        ],
        bgClass: 'bg-[hsl(220_13%_10%)] text-white',
    },
    {
        step: 2,
        icon: Sparkles,
        title: 'L\'AI elabora la bozza',
        time: '~30 secondi',
        body: 'Claude (Anthropic) analizza la tua richiesta sulla base del D.Lgs 81/2008 e genera in tempo reale: lista dei rischi specifici (con livello basso/medio/alto), misure di prevenzione, DPI obbligatori, formazione richiesta, documenti obbligatori. Contemporaneamente classifica la complessità del lavoro (Standard / Intermedio / Complesso) e calcola il prezzo.',
        tips: [
            'Easy = lavori brevi e a bassa quota (€149 base)',
            'Medium = ristrutturazioni medie (€299 base)',
            'Complex = cantieri grandi, lavori >6m, demolizioni (€549 base)',
            'Tutti i prezzi sono in chiaro PRIMA di pagare',
        ],
        bgClass: 'bg-[hsl(45_100%_97%)]',
    },
    {
        step: 3,
        icon: CreditCard,
        title: 'Conferma e paga online',
        time: '~30 secondi',
        body: 'Vedi l\'anteprima dei rischi identificati, la motivazione dell\'AI, lo SLA di consegna e l\'importo finale (IVA inclusa). Spunti i 2 consensi obbligatori (termini + privacy) e clicchi "Paga e procedi". Vieni reindirizzato a Stripe Checkout per pagare in modo sicuro con Carta di Credito, Apple Pay o Google Pay.',
        tips: [
            'Stripe 3D-Secure: nessun dato carta passa sui nostri server',
            'Conferma del pagamento immediata',
            'Pagamento rifiutato? Nessun addebito, puoi riprovare',
        ],
        bgClass: 'bg-[hsl(210_90%_97%)]',
    },
    {
        step: 4,
        icon: UserCheck,
        title: 'Il professionista revisiona',
        time: 'In base allo SLA',
        body: 'Appena confermato il pagamento ricevi via email i dettagli del professionista assegnato (RSPP / CSP / CSE) e creiamo automaticamente il tuo account su CantiereAI. Il professionista esamina la bozza, esegue eventualmente un sopralluogo (incluso per cantieri complessi), integra il documento con osservazioni tecniche specifiche e firma digitalmente.',
        tips: [
            'Riceverai una notifica via email all\'inizio della revisione',
            'Il professionista può contattarti per chiarimenti se serve',
            'Tutte le integrazioni sono incluse nel prezzo iniziale',
        ],
        bgClass: 'bg-[hsl(145_45%_97%)]',
    },
    {
        step: 5,
        icon: Mail,
        title: 'Ricevi il DVR firmato',
        time: 'Entro lo SLA',
        body: 'Il PDF con timbro e firma digitale del professionista ti arriva via email. Il documento è giuridicamente valido ai fini del D.Lgs 81/2008 e può essere mostrato a ispezioni INAIL, ASL, Vigili del Fuoco e in qualsiasi cantiere. Una copia è sempre disponibile nella tua area riservata su CantiereAI (sezione Sicurezza).',
        tips: [
            'Conservalo insieme alla documentazione di cantiere',
            'Vale come prova di conformità in caso di ispezioni',
            'Modifiche minori in caso di varianti? Contattaci via email',
        ],
        bgClass: 'bg-[hsl(var(--accent))]/15',
    },
];

export default function DvrComeFunziona() {
    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="dvr-how-page">
            <nav className="sticky top-0 z-40 bg-[hsl(var(--background))]/95 backdrop-blur border-b border-[hsl(var(--border))]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Logo />
                    <Link to="/dvr">
                        <Button variant="outline" className="gap-2" data-testid="dvr-how-back">
                            <ArrowLeft className="w-4 h-4" /> Torna alla pagina principale
                        </Button>
                    </Link>
                </div>
            </nav>

            {/* HEADER */}
            <section className="bg-[hsl(220_13%_10%)] text-white py-12 md:py-16">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold mb-3">GUIDA COMPLETA</Badge>
                    <h1 className="font-display text-4xl md:text-6xl mb-3">Come funziona il servizio DVR</h1>
                    <p className="text-white/80 max-w-2xl mx-auto">5 step per passare da "ho un cantiere" a "ho il DVR firmato dal professionista" in tempi certi e a prezzo trasparente.</p>
                </div>
            </section>

            {/* TIMELINE */}
            <section className="py-12 md:py-20 max-w-4xl mx-auto px-4">
                <div className="space-y-8">
                    {SECTIONS.map((s) => {
                        const Icon = s.icon;
                        return (
                            <div key={s.step} className="relative" data-testid={`dvr-how-step-${s.step}`}>
                                <Card className={`p-6 md:p-8 ${s.bgClass} border-2`}>
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-14 h-14 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center text-[hsl(220_13%_10%)] font-display text-2xl flex-shrink-0">
                                            {s.step}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-2">
                                                <Icon className="w-5 h-5 text-[hsl(var(--accent))]" />
                                                <h2 className="font-display text-2xl md:text-3xl">{s.title}</h2>
                                                <Badge variant="outline" className="ml-auto"><Clock className="w-3 h-3 mr-1" /> {s.time}</Badge>
                                            </div>
                                            <p className="text-sm md:text-base leading-relaxed opacity-90">{s.body}</p>
                                        </div>
                                    </div>
                                    <div className="ml-0 md:ml-[72px] mt-4 pt-4 border-t border-current/10">
                                        <div className="text-xs uppercase tracking-wider opacity-60 mb-2">Consigli</div>
                                        <ul className="space-y-1.5 text-sm">
                                            {s.tips.map((tip, i) => (
                                                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" /> {tip}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </Card>
                                {/* Vertical connector */}
                                {s.step < SECTIONS.length && (
                                    <div className="flex justify-center my-2">
                                        <ArrowRight className="w-5 h-5 text-[hsl(var(--muted-foreground))] rotate-90" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Legal notice */}
                <Alert className="mt-10 border-[hsl(45_100%_60%)] bg-[hsl(45_100%_97%)]" data-testid="dvr-how-legal">
                    <AlertTriangle className="h-4 w-4 text-[hsl(35_90%_35%)]" />
                    <AlertDescription className="text-sm">
                        <strong>Importante:</strong> il documento prodotto è una <strong>bozza tecnica generata dall'AI</strong>. La validità legale ai sensi del D.Lgs 81/2008 si ottiene SOLO dopo la revisione e firma digitale del professionista abilitato (RSPP / CSP / CSE) — passaggio incluso nel prezzo del servizio.
                    </AlertDescription>
                </Alert>
            </section>

            {/* DOWNLOAD GUIDE */}
            <section className="py-12 bg-[hsl(var(--muted))]/30">
                <div className="max-w-3xl mx-auto px-4 text-center">
                    <Download className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--accent))]" />
                    <h3 className="font-display text-2xl mb-2">Vuoi questa guida da scaricare?</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">Scarica la guida in formato Markdown (stampabile) da inviare ai tuoi clienti o conservare per riferimento.</p>
                    <a href="/guides/GUIDA_CLIENTE_DVR.md" download>
                        <Button variant="outline" className="gap-2" data-testid="dvr-how-download">
                            <Download className="w-4 h-4" /> Scarica guida (Markdown)
                        </Button>
                    </a>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-16 bg-[hsl(220_13%_10%)] text-white text-center">
                <div className="max-w-3xl mx-auto px-4">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--accent))]" />
                    <h2 className="font-display text-3xl md:text-5xl mb-3">Tutto chiaro?</h2>
                    <p className="text-white/70 mb-8">Inizia la richiesta ora, vedi il prezzo prima di pagare.</p>
                    <Link to="/dvr#richiedi">
                        <Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12 px-8 text-base gap-2" data-testid="dvr-how-cta">
                            <Stamp className="w-5 h-5" /> Richiedi DVR ora
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
}
